/**
 * Excursion Table Widget
 * Searchable table matching the screenshot
 */

import { useState } from 'react'
import { MagnifyingGlass, Funnel } from '@phosphor-icons/react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { ExcursionData } from '../mock-excursion-data'

interface ExcursionTableWidgetProps {
  data: ExcursionData[]
  usesCustomScope?: boolean
  isEditMode?: boolean
}

export function ExcursionTableWidget({ data, usesCustomScope, isEditMode }: ExcursionTableWidgetProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredData = data.filter(
    (exc) =>
      searchQuery === '' ||
      exc.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exc.product.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exc.headline.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High':
        return 'bg-[#fee7e7] text-[#b41e2e]'
      case 'Medium':
        return 'bg-[#fff7ed] text-[#c83408]'
      case 'Low':
        return 'bg-[#ffffe7] text-[#846609]'
      default:
        return 'bg-[#f5f5f5] text-[#272727]'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open':
        return 'bg-[#fee7e7] text-[#b41e2e]'
      case 'In Progress':
        return 'bg-[#fff7ed] text-[#f04905]'
      case 'Resolved':
        return 'bg-[#dcfde8] text-[#12833d]'
      default:
        return 'bg-[#f5f5f5] text-[#272727]'
    }
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-[15px] border border-[#e6e6e6] bg-white">
      {usesCustomScope && !isEditMode && (
        <div className="absolute right-5 top-5 z-10">
          <div className="flex items-center rounded-3xl border border-[#ff8637] bg-[#fff7ed] px-2 py-1">
            <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#c83408]">
              Custom Scope
            </span>
          </div>
        </div>
      )}

      <div className="px-5 pt-5">
        <h3 className="text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]">Excursion Details</h3>
      </div>

      {/* Search bar */}
      <div className="border-b border-[#e6e6e6] bg-white px-5 py-4">
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#727272]" />
          <Input
            type="text"
            placeholder="Search by ID, product, severity, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-10 rounded-xl border-[#e6e6e6] pl-11 text-base tracking-[0.16px] placeholder:text-[#727272] focus-visible:border-[#ff5c04] focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-[#d3d3d3]"
          />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs tracking-[0.12px] text-[#727272]">
            Showing {filteredData.length} of {data.length} excursions
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-[#727272] hover:text-[#272727]"
          >
            <Funnel className="mr-1 h-3 w-3" />
            Columns
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#fafafa]">
            <tr className="border-b border-[rgba(0,0,0,0.06)]">
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Ex. ID</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Date</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Severity</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Status</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Product</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Lots</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Wafers</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Yield Loss</th>
              <th className="px-4 pb-[17px] pt-4 text-left text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Headline</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((exc) => (
              <tr
                key={exc.id}
                className="border-b border-[rgba(0,0,0,0.06)] bg-white transition-colors hover:bg-[#fafafa]"
              >
                <td className="px-4 pb-[17px] pt-4">
                  <span className="text-sm font-medium tracking-[0.14px] text-[#1d1d1d]">{exc.id}</span>
                </td>
                <td className="px-4 pb-[17px] pt-4 text-sm tracking-[0.14px] text-[#535353]">{exc.date}</td>
                <td className="px-4 pb-[17px] pt-4">
                  <div className={`inline-flex items-center gap-2 rounded-3xl px-2 py-1 ${getSeverityColor(exc.severity)}`}>
                    <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px]">{exc.severity}</span>
                  </div>
                </td>
                <td className="px-4 pb-[17px] pt-4">
                  <div className={`inline-flex items-center gap-2 rounded-3xl px-2 py-1 ${getStatusColor(exc.status)}`}>
                    <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px]">{exc.status}</span>
                  </div>
                </td>
                <td className="px-4 pb-[17px] pt-4 text-sm font-medium tracking-[0.14px] text-[#1d1d1d]">{exc.product}</td>
                <td className="px-4 pb-[17px] pt-4 text-center text-sm tracking-[0.14px] text-[#535353]">{exc.lots}</td>
                <td className="px-4 pb-[17px] pt-4 text-center text-sm tracking-[0.14px] text-[#535353]">{exc.wafers}</td>
                <td className="px-4 pb-[17px] pt-4 text-sm tracking-[0.14px] text-[#535353]">{exc.yieldLoss}</td>
                <td className="px-4 pb-[17px] pt-4 text-sm tracking-[0.14px] text-[#535353]">{exc.headline}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
